import { MachinePublic } from "@aura-frontend/omnibus-az-client";

import { AlertMessage } from "@/components/ui/AlertMessage";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useMachines } from "@/hooks/useMachines";
import { useRegions } from "@/hooks/useRegions";

type MachineSelectorProps = {
  /** If true, displays the region selector for global machine search */
  showRegionSelector: boolean;
  /** Selected region (required when showRegionSelector is true) */
  selectedRegion?: string;
  /** Currently selected machine ID */
  selectedMachineId: string;
  /** Callback when region changes (required when showRegionSelector is true) */
  onRegionChange?: (value: string) => void;
  /** Callback when machine selection changes */
  onMachineChange: (value: string) => void;
  /** If true, only fetches machines with tagging capability. Default: true */
  isTaggingMachines?: boolean;
};

export const MachineSelector = ({
  showRegionSelector,
  selectedRegion,
  selectedMachineId,
  onRegionChange,
  onMachineChange,
  isTaggingMachines = true,
}: MachineSelectorProps) => {
  const { regions, loadingRegions, regionError } = useRegions({
    enabled: showRegionSelector,
  });

  // When showing region selector, filter by selected region
  // Otherwise, fetch all machines for the user
  const { machines, loadingMachines, machinesError } = useMachines({
    region: showRegionSelector ? selectedRegion : undefined,
    enabled: !showRegionSelector || !!selectedRegion,
    isTaggingMachines,
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {showRegionSelector && (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Region</span>
            <SearchableSelect
              value={selectedRegion ?? ""}
              onValueChange={onRegionChange!}
              options={regions.map((region: string) => ({
                value: region,
                label: region,
                id: region,
              }))}
              placeholder={
                loadingRegions ? "Loading regions..." : "Select a region..."
              }
              isLoading={loadingRegions}
            />
          </div>
        )}
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">System</span>
          <SearchableSelect
            value={selectedMachineId}
            onValueChange={onMachineChange}
            options={
              (machines as MachinePublic[])?.map((m) => ({
                value: m.machineUuid,
                label: m.name,
                id: m.machineUuid,
              })) ?? []
            }
            placeholder={
              loadingMachines
                ? "Loading systems..."
                : showRegionSelector && !selectedRegion
                  ? "Select a region first"
                  : "Select a system..."
            }
            isLoading={loadingMachines}
            disabled={showRegionSelector && !selectedRegion}
          />
        </div>
      </div>

      {regionError && showRegionSelector && (
        <AlertMessage
          variant="destructive"
          title="Unable to load regions"
          description="There was a problem loading the available regions. Please refresh the page or try again later."
        />
      )}
      {machinesError && (!showRegionSelector || selectedRegion) && (
        <AlertMessage
          variant="destructive"
          title="Unable to load systems"
          description={
            showRegionSelector
              ? `Failed to load systems for ${selectedRegion}. Please try selecting a different region or refresh the page.`
              : "Failed to load systems. Please refresh the page or try again later."
          }
        />
      )}
    </div>
  );
};
